import { FieldValues, Path, useFormContext } from 'react-hook-form';
import { useCallback, useMemo, useState } from 'react';
import { useCustomTranslation } from '../../../hooks';
import { useUsers } from '../../../queryHooks/useUsers';
import { FormSelectInput } from '@nexelem/form-components';
import { formatName } from '../../../utils';
import { TableAvatarWithName } from '../../atoms/TableAvatarWithName';

type FormAcceptorSelectProps<Value extends FieldValues> = {
  readonly name: Path<Value>;
  readonly isMultiple?: boolean;
  readonly label?: string;
  readonly placeholder?: string;
};

export const FormAcceptorSelect = <Value extends FieldValues>({
  label,
  placeholder,
  ...props
}: FormAcceptorSelectProps<Value>) => {
  const { t } = useCustomTranslation('warehouse.stocktaking.columns');
  const [isOpen, setIsOpen] = useState(false);
  const onToggle = useCallback(() => setIsOpen((prevState) => !prevState), []);
  const [text, setText] = useState('');

  const { control } = useFormContext<Value>();
  const { data, isLoading } = useUsers();

  const options = useMemo(
    () =>
      data?.map(({ firstName, lastName, id = 0 }) => {
        return { value: id, key: formatName(firstName, lastName) };
      }),
    [data],
  );

  return (
    <FormSelectInput
      {...props}
      control={control}
      getOptionKey={({ value }) => value.toString()}
      getOptionLabel={({ key }) => key.toString()}
      inputValue={text}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      label={label}
      loading={isLoading}
      open={isOpen}
      options={options}
      placeholder={placeholder ?? t('components.filters.searchOrSelect')}
      renderOption={(props, option) => (
        <li {...props}>
          <TableAvatarWithName fullName={option.key} />
        </li>
      )}
      onClose={onToggle}
      onInputChange={setText}
      onOpen={onToggle}
    />
  );
};
